import { OutdoorSportsDetailData, OutdoorSportsLegendData } from '@weatherbug/common/dist/data-layer/actions/outdoorSportsActions';
import type { HealthAdviceTypes, PollutantType } from '../../airQuality/v2/types';
import { FeatureCollection } from '@turf/helpers';

import { utilities } from '@weatherbug/common';

/* TODO: We should be able to import types from commonjs differently without pulling from ../dist/...  */
import type { HurricaneOutlookSummaryPayload } from '@weatherbug/common/dist/data-layer/types/APIResponses/Hurricane/Outlook/hurricaneOutlookSummaryResponses';
import type { HurricaneOutlookBulletinPayload } from '@weatherbug/common/dist/data-layer/types/APIResponses/Hurricane/Outlook/hurricaneOutlookBulletinResponses';
import type { MapAdRefreshState } from '../../../common/reducers/mapAdRefreshReducer';
import type { NewsStoriesState } from '../../../common/reducers/stories/storiesReducer';
import { string } from 'prop-types';
import { StatePrivacy } from '../../../common/reducers/statePrivacyReducer';
const { speedUnits, pressureUnitLongAbbreviations, distanceUnitsEnum, distanceUnits } = utilities.units;

export interface Locality {
    Latitude: number;
    Longitude: number;
    SlugName: string;
    CityId: string;
    CityName: string;
    DisplayCompositeName: string;
    GeographicCompositeName: string;
    TerritoryName: string;
    TerritoryAbbr: string | null;
    CountryShortName: string;
    CountryIso2Code: string;
    CountryIso3Code: string;
    PostalCode: string | null;
    Dma: string  | null;
    RouteString: string;
    Label: string;
}

export interface Station {
    StationId: string;
    ProviderId: string;
    ProviderName: string;
    StationName: string;
    Latitude: number;
    Longitude: number;
    Distance: number;
    ElevationAboveSeaLevelMeters: string;
    RouteString: string;
}

export interface WbLocation {
    Id: string;
    Locality: Locality;
    Station: Station;
    DefaultStationRouteString: string;
}

export interface FavoriteLocation {
    Id: string;
    Position: number;
    Location: WbLocation;
}

export interface User {
    Language: string;
    Id: string;
    ActiveLocation: WbLocation;
    LastSearchedLocations: WbLocation[];
    FavoriteLocations: WbLocation[];
    Units: number;
    TempUnit: number;
    WindUnit: number;
    PressureUnit: number;
    Gender: string;
    BirthDate: string | null;
    DeviceId: string;
    AppInstanceIdRegistrationDateUtc: string;
    Os: string;
    OsVersion: string;
    IsBot: boolean;
    Age: number;
    GenderValue: number;
    AgeValue: number;
    AccessToken?: string;
    RefreshToken?: string;
    Username?: string;
}

export interface MapLayerData {
    filter: (string | (string | boolean)[])[];
    id: string;
    source: string;
    layerType: string;
    iconImage: string;
    selectedFeatureIconImage: string;
}

interface Layer {
    label?: string | null;
    iconResourceId: string;
    alpha: number;
    attribution: unknown[];
    adId: string;
    id: string;
    vId: string;
    dataFormat: string;
    minZoom: number;
    maxZoom: number;
}

interface DefaultSlot {
    id: string;
    time: number;
}

interface Animation {
    canLoop: boolean;
    interval: number;
    loopDelay: number;
    slots: DefaultSlot[];
}

export interface MapMetaDataValues {
    layer: Layer;
    defaultSlot: DefaultSlot;
    canAnimate: boolean;
    animation: Animation;
    timestamp: number;
    token: string;
    responseTime: number;
}

export interface IStdParameters {
    appId: string;
    application: string;
    appInstanceId: string;
    appVersion: string;
    cultureInfo: string;
    formFactor: string;
    os: string;
    osVersion: string;
    userAgent: string;
}

interface PlatformItem {
    Header: boolean;
    Ads: boolean;
    Footer: boolean;
    SmartBanner: boolean;
    Navigation: boolean;
    ShowMapOverlays: boolean;
    Notifications: boolean;
    UseOfCookiesNotification: boolean;
    TermsAndConditionsNotification:boolean;
    ShowEmailOptInWidget: boolean;
    EnableKochavaSdk: boolean;
}

export type PlatformKeys = 'ach' | 'aw' | 'dt' | 'eow' | 'ki' | 'lh' | 'ma' | 'ma1' | 'md1' | 'md2' | 'mi' | 'mi1' | 'wbw' | 'wd';
export type PlatformType = Record<PlatformKeys, PlatformItem>;

export type RouteParamsType = {
    id?: string;
    location?: string;
    [key: string]: string | undefined;
};

export interface IAppState {
    airQualityAttributionUrl: string;
    appId: string;
    appInstanceId: string;
    appVersion: string;
    authId: string;
    countryCode: string;
    defaultMapLayerId: string;
    enableAds: boolean;
    enableNotifications: boolean;
    enableTaboola: boolean;
    enableTermsAndConditionsNotification: boolean;
    enableUseOfCookiesNotification: boolean;
    enableSourceParameterOnStoriesLinks: boolean;
    leadingEdgeDebounceDelay: number;
    defaultAdRefeshIntervalSeconds: number;
     mapsPageRefreshTimer: number;
    fireBaseUrl: string;
    formFactor: string;
    adTargetingBaseUrl: string;
    hurricaneBaseUrl: string;
    hurricaneOutlookBaseUrl: string;
    alertsBaseUrl: string;
    outdoorSportsBaseUrl: string;
    notificationsBaseUrl: string;
    lightningBaseUrl: string;
    loggingBaseUrl: string;
    aqiBaseUrl: string;
    forecastBaseUrl: string;
    locationBaseUrl: string;
    obsBaseUrl: string;
    pollenBaseUrl: string;
    almanacBaseUrl: string;
    uvBaseUrl: string;
    contentMgmtBaseUrl: string;
    legalBaseUrl: string;
    storiesBaseUrl: string;
    snowSkiBaseUrl: string;
    videoBaseUrl: string;
    feedbackBaseUrl: string;
    sparkMapBaseUrl: string;
    forecastIconResourceList: string;
    forecastIconSize: string;
    forecastIconSet: string;
    languageCode: string;
    mapBaseUrl: string;
    mapboxAccessToken: string;
    os: string;
    osVersion: string;
    platformCode: string;
    secret: string;
    tileBaseUrl: string;
    userAgent: string;
    userLocale: string;
    user: User;
    stdParameters: IStdParameters;
    routePath: string;
    routeParams: RouteParamsType;
    pageFooterSetup: string;
    pageMetaData: PageMetaData;
    locality: Locality;
    pageId: string;
    popupId?: string;
    pageName: string;
    busDomain: string;
    authClientSettings: AuthClientSettings;
    emLoginUrl: string;
    appKey: string;
    isAuthV2Enabled: boolean;
    tempUnit: string;
    speedUnit: string;
    isAqiV2Enabled: boolean;
    isOutdoorSportsEnabled: boolean;
    gTMediaCampaignIsEnabled: boolean;
    comscoreConfig: Comscore;
    firebaseConfig: Firebase;
    boltiveConfig: Boltive;
    gamTestAdConfig: GamTestAd;
    homePagePixelImgSourceUrl: string;
    appDownloadPagePixelImgSourceUrl: string;
    activeRoute: string;
    hurricaneOutlookDetailsImages: HurricaneBulletinImageIds;
    platform: PlatformType;
    enableNewFooterUIWithCMPIntegration: boolean;
    usPrivacyManagerId: number;
    sourcePointCMPAccountId: number;
    sourcePointCMPPropertyId: number;
    sourcePointCMPPropertyHref: string;
    SourcePointCMPBaseEndpoint: string;
    SourcePointCMPCampaignEnv: string;
    persistent320x50AdForMobileWeb: Persistent320x50AdForMobileWeb;
    enableNewSmartBanner: boolean;
    kochavaClickURL: string;
    sourceProviderId: string;
    enable10DayForecastPageOnWeb2: boolean;
    enableHourlyForecastPageOnWeb2: boolean;
    enableMapsPageOnWeb2: boolean;
    enableNewsLandingPageOnWeb2: boolean;
    enableLifePollenPageOnWeb2: boolean;
    enableLifeColdFluPageOnWeb2: boolean;
    enableAirQualityLandingPageOnWeb2: boolean;
    enableVideoLandingPageOnWeb2: boolean;
    enableAlertsAQIPageOnWeb2: boolean;
    enableAlertsSevereStormPageOnWeb2: boolean;
    enableAlertsAllPageOnWeb2: boolean;
    enableFireAlertsPageOnWeb2: boolean;
    enableHurricaneAlertsPageOnWeb2: boolean;
    enableTrafficCamerasPageOnWeb2: boolean;
    enableWeatherCamerasPageOnWeb2: boolean;
    enableAlertsPollenPageOnWeb2: boolean;
    enableAlertsWeatherPageOnWeb2: boolean;
    enableAlertsLightningPageOnWeb2: boolean;
    enableEducationPageOnWeb2: boolean;
    enableFaqAndFaqGeneralPageOnWeb2: boolean;
}

export interface ICoreAppState {
    isFetchingAppState: boolean;
    isSettingAppState: boolean;
    lastError: Record<string, unknown> | string;
    appState: IAppState;
}

export type AqiTopTriggerHealthAdviceDetailsModalState = {
    isTopTriggerHealthAdviceDetailPopupOpen: boolean;
    selectedAqiHealthAdvice?: keyof typeof HealthAdviceTypes;
    selectedAqiPollutant?: keyof typeof PollutantType;
}

export interface IStoreState {
    appState: ICoreAppState;
    airQuality: AirQuality;
    outdoorSports: OutdoorSports;
    newsStories: NewsStoriesState;
    units: Units;
    aqiTopTriggerHealthAdviceDetailModal: AqiTopTriggerHealthAdviceDetailsModalState;
    nowConditions: {
        nowConditions: NowConditions;
    };
    alerts: {
        notifications: PushNotificationsResponse;
    };
    outdoorSportsDetailData: OutdoorSportsDetailData;
    outdoorSportsLegendData: OutdoorSportsLegendData;
    outdoorSportsSummaryData: OutdoorSportsSummaryResult;
    stories: Stories;
    video: Videos;
    forecast: ForecastDailyStoreState;
    weatherCam: WeatherCam;
    hurricane: Hurricane;
    hurricaneOutlookSummary: hurricaneOutlookSummaryStateType; 
    hurricaneOutlookBulletin: HurricaneOutlookBulletinStateType;
    legal: Legal;
    favoriteLocations: FavoriteLocationsState;
    mapAdRefresh: MapAdRefreshState;
    pageVisibility: {
        visible: boolean;
    };
    boltive: {
        shouldLoadBoltiveTag: boolean;
    };
    statePrivacy: StatePrivacy;
}

export interface PulseBaseOptions {
    timeout: number; 
    retryOptions: { enableRetry: boolean; retryCount: number; };
    emToken: string; 
}

export interface PulseBaseLogger {
    error: () => void;
}

export interface UserInfo {
    FirstName: string;
    LastName: string;
    Email: string;
    BirthDate: string;
    Gender: string;
}

export interface Settings {
    Language: string;
    UnitType: string;
    PressureUnitType: string;
    WindUnitType: string;
    DateTimeFormat: string;
    TemperatureUnit: string;
    DistanceUnit: string;
    PrecipitationUnit: string;
    SpeedUnit: string;
    PressureUnit: string;
    DirectionUnit: string;
}

export interface EmUserAddress {
    City: string;
    Zip: string;
    State: string;
    Country: string;
    Dma: string;
}

export interface EmUserLocationCoordinates {
    Latitude: number;
    Longitude: number;
}

export interface EmUserLocationNotificationOptions {
    IsNotificationEnabled: boolean;
    CustomNotifications: Record<string, unknown>;
}
export interface EmUserProfileLocation {
    Id: string;
    Name: string;
    Station: Station | null;
    Address: EmUserAddress;
    Coordinates: EmUserLocationCoordinates;
    NotificationOptions: EmUserLocationNotificationOptions;
}

export interface EmUserProfile {
    UserInfo: UserInfo;
    Settings: Settings;
    Locations: EmUserProfileLocation[];
    responseTime?: string;
}

export interface IAuthUserInfo {
    userName: string;
    accessToken: string;
    refreshToken: string;
}

export interface AqiData {
    index: number;
    color: string;
    description: string;
    todaysConditions: string;
}

export interface LocalAqiData extends AqiData {
    name: string;
    indexPrefix: string;
}

export interface AirQualityRecommendations {
    children: string;
    sport: string;
    health: string;
    inside: string;
    outside: string;
}

export interface DominantPollutant {
    particulateType: number;
    canonicalName: string;
    description: string;
    pollutantDetails: PollutantDetails;
}

export interface PollutantDetails {
    main: string;
    effects: string;
    causes: string;
}

export interface Pollutant {
    particulateType: number;
    description: string;
    units: string;
    concentration: number;
}

export interface Pollutants {
    co: Pollutant;
    no2: Pollutant;
    o3: Pollutant;
    pm10: Pollutant;
    pm25: Pollutant;
    so2: Pollutant;
}

export interface CurrentAirQuality {
    timeStampUtc: number;
    aqi: AqiData;
    localAQI: LocalAqiData;
    recommendations:AirQualityRecommendations;
    dominantPollutant: DominantPollutant;
    pollutants: Pollutants;
    isCached: boolean;
    pointOfInterestName: string;
    distance: number;
    responseTime: number;
}

export interface AirQuality {
    currentAirQualityIsRefreshing: boolean;
    forecastIsRefreshing: boolean;
    currentAirQualityIsFetching: boolean;
    lastError: Record<string, unknown>;
    currentAirQuality: CurrentAirQuality;
}

export interface Units {
    lastError: Record<string, unknown>;
    pressureUnit: number;
    rainUnit: number;
    tempUnit: number;
    unitSystem: number;
    unitsIsLoading: boolean;
    windUnit: number;
}

export interface NowConditions {
    ic: number;
    d: string;
    t: number;
    flt: number;
    ht: number;
    lt: number;
    cdtl: string;
    sn: string;
    responseTime: number;
}

export enum HourlyForecastRoundingProfiles {
    None = 'none', // values returned as is without any rounding
    Precise = 'precise', // all values are rounded to 4 decimal places
    DisplayLegacyV1 = 'displayLegacyV1', // implements rounding approach used by the API before adding 'rounding' parameter
    DisplayV1 = 'displayV1' // implements rounding approach to produce values with precision required to display values on UI in client applications
}

export interface PushNotificationsResponse {
    aid: string;
    eml: string;
    nl: Alert[];
    c: number;
}

export interface Alert {
    id: string;
    idt: string;
    edt: string;
    tz: string;
    nm: string;
    msg: string;
    ns: string;
    ni: Icon;
    nt: string;
    not: string;
    nmd: Nmd;
}

export interface Icon {
    ids: State;
    irs: State;
}

export interface State {
    sicu: string;
    sbgd: Attribute;
    ssym: Attribute;
}

export interface Attribute {
    afc: Color;
    asc: Color;
    aws: number;
}

export interface Color {
    ch6: string;
    opa: number;
}

export interface Nmd {
    nmklv: string;
    nmkln: string;
    nmklp1: string;
}

export interface OutdoorSportsSummarySafetyTip {
    safetyTips: string;
    safetyIcon: string;
    indexNumber: number;
    indexLabel: string;
    color: string;
}
export interface OutdoorSportsSummaryResult {
    responseTime: number;
    safetyTip: OutdoorSportsSummarySafetyTip;
}

export interface OutdoorSportsSummary {
    id: string;
    errorMessage?: string;
    result: OutdoorSportsSummaryResult;
    code: number;
}

export interface OutdoorSportsIndexLegendLevel {
    indexLevelMinimum: number;
    indexLevelMaximum: number;
    indexLevelLabel: string;
    indexLevelDisplayStr: string;
    indexLevelDescription: string;
    indexLevelIcon: string;
    indexLevelColor: string;
}
export interface OutdoorSportsLegendDataType {
    outdoorSportsIndexLegendLevels: OutdoorSportsIndexLegendLevel[];
    responseTime: number;
}

export interface OutdoorSportsKeyIndicatorsType {
    sunriseDateTimeUtc: string;
    sunriseDateTimeOffsetLcl: string;
    sunsetDateTimeUtc: string;
    sunsetDateTimeOffsetLcl: string;
    sunriseDisplayStr: string;
    sunsetDisplayStr: string;
    feelsLikeTemperature: number;
    wetBulbTemperatureMeasured: number;
    wetBulbTemperatureCalculated?: string;
    wetBulbTemperatureDisplayStr: string;
    wetBulbDisclaimer?: string;
    isWetBulbTemperatureEstimated: boolean;
    precipProbabilityPercent100: number;
    humidityPercent100: number;
    uvDisplayStr: string;
    uvLabel: string;
    uvNumber: number;
    windDisplayStr: string;
    windDirection: string;
    windSpeed: number;
    windSpeedUnitDisplayStr: string;
}

export interface OutdoorSportsForecastRecordType {
    indexNumber: number;
    color: string;
    forecastIcon: number;
    temperature: number;
    precipProbabilityPercent100: number;
    dateTimeHour: number;
    dateTimeHourDisplayStr: string;
    dateTimeDayOfWeekDisplayStr: string;
    outdoorIndexDateTimeOffsetLcl: string;
    isDayTime: boolean;
    displayLabelOnChart: boolean;
}

export interface OutdoorSportsDetailDataType {
    safetyTip: OutdoorSportsSummarySafetyTip;
    keyIndicators: OutdoorSportsKeyIndicatorsType;
    forecastRecords: OutdoorSportsForecastRecordType[];
}

export interface LocationStringData {
    StringData: string;
}

export interface Story {
    Source: unknown;
    ContentTags: string[];
    NodeAlias: string;
    ByLine: string;
    Summary: string;
    Title: string;
    Text: string;
    ImageLink: string;
    ThumbnailImageLink: string;
    CreatedWhen: string;
    ModifiedWhen: string;
    PublishWhen: string;
    CreatedDate: string;
    ModifiedDate: string;
    PublishDate: string;
    Location: LocationStringData;
    ImageCaption: string;
    Id: number;
    Guid: string;
    ClassName: string;
    VideoLinks: unknown;
}

export interface Stories {
    lastError?: unknown;
    stories: Story[];
    storiesIsFetching: boolean;

}

export interface OutdoorSports {
    error: Record<string, unknown>;
    outdoorSportsDataIsFetching: boolean;
    outdoorSportsDetailData: OutdoorSportsDetailDataType;
    outdoorSportsLegendData: OutdoorSportsLegendDataType;
    outdoorSportsSummaryData: OutdoorSportsSummaryResult;
}

export const WbStatePrivacyCookieKey = 'wb_state_privacy';

export interface StatePrivacyPolicyCookieType {
    doNotSell?: boolean;
    limitUseOfPI?: boolean;
}

export interface NewPrivacyPolicyMockData {
    IsEnabled: boolean;
    DataPrivacyEnabled: boolean;
    SensitiveDataRestrictionEnabled: boolean;
}

export interface Persistent320x50AdForMobileWeb {
    ConfiguredPagesWithPersistent320x50Ads: Persistent320x50AdDetailsForPage[];
}

export type Persistent320x50AdDetailsForPage = {
    IsEnabled: boolean;
    PageId: string;
    SlotId: string;
}

export type FireType = { 
    activeFires: FeatureCollection; 
    localFireInfo: LocalFireInfo; 
    selectedFire: FeatureCollection;
    lastError: Record<string, unknown>;
}

export interface LocalFireInfo {
    forecasts: Fire[];
    closestFire: ClosestFire;
    fireMessage: string;
    lastUpdatedDateTime: number;
    responseTime: number;
}

export interface Fire {
    // features should be a FeatureCollection, but that is a union type and
    // we can't do any array methods against it if we type it that way here
    features: unknown;
    dayOfWeekLocal: string;
    dayOfWeekAbbreviatedLocal: string;
    fireRiskIndex: number;
    fireRiskDescriptionLong: string;
    fireRiskDescriptionShort: string;
    fireRiskReasons: [];
}

export interface ClosestFire {
    distance: number;
    distanceUnit: number;
    centerLatitude: number;
    centerLongitude: number;
    formattedDistanceStr: string;
}

export type PrevLatLngType = {
    latitude: number;
    longitude: number;
};
export interface Videos {
    videosIsFetching: boolean;
    pageNumber: number;
    totalVideoCount: number;
    videos: Video[];
    lastError: Record<string, unknown>;
}

/**
 * This is interface for Video in compact form.
 * 
 * (compactPropertyName  ---> VerbosePropertyName)
 * 
 * i ---> id,
 * t ---> title,
 * d ---> description,
 * s ---> summary,
 * aid ---> analyticsId,
 * cuts ---> createdUnixTimestampSec,
 * muts ---> modifiedUnixTimestampSec,
 * puts ---> publishedUnixTimestampSec,
 * thumb ---> thumbnailUri,
 * poster ---> posterImageUri,
 * cat ---> primaryCategory,
 * cats ---> additionalCategories,
 * atts ---> attributions,
 * lks ---> links,
 * tgs ---> tags,
 * plid ---> permalinkId,
 * cutss ---> createdUnixTimestampSecString,
 * mutss ---> modifiedUnixTimestampSecString,
 * putss ---> publishedUnixTimestampSecString
 */
export interface Video {
    i: string;
    t: string;
    d: string;
    s: string;
    aid: string;
    cuts: number;
    muts: number;
    puts: number;
    thumb: string;
    poster: string;
    cat: string;
    cats: unknown[];
    atts: unknown[];
    lks: VideoLink[];
    tgs: string[];
    plid: string;
    cutss: string;
    mutss: string;
    putss: string;
}

/**
 *  VideoLink in compact form
 * (compactPropertyName  ---> VerbosePropertyName)
 * 
 * res ---> resolution,
 * uri ---> uri
 */
interface VideoLink {
    res: '720p' | '540p' | '360p';
    uri: string;
}

export interface WidgetProps {
    widgetLinkURL?: string;
    handleWidgetClick?: () => void;
}

/**
 * DailyForecastPeriod in compact form
 * (compactPropertyName  ---> VerbosePropertyName)
 * dd ---> detailedDescription,
 * intp ---> isNightTimePeriod,
 * sd ---> summaryDescription,
 * fdls ---> forecastDateLocalStr,
 * fdus ---> forecastDateUtcStr,
 * ic ---> iconCode,
 * t ---> temperature
 */
export interface DailyForecastPeriod {
    dd: string;
    intp: boolean;
    sd: string;
    fdls: string;
    fdus: string;
    ic: number;
    t: string;
}

/**
 * DailyForecast in compact form
 * (compactPropertyName  ---> VerbosePropertyName)
 * dfp ---> dailyForecastPeriods,
 * fcu ---> forecastCreatedUtcStr,
 * l ---> location,
 * lt ---> locationType
 */
export interface DailyForecast {
    dfp: DailyForecastPeriod[];
    fcu: string;
    l: string;
    lt: string;
}

export interface ForecastDailyStoreState {
    dailyForecastIsRefreshing: boolean;
    daily: DailyForecast;
    lastError: Record<string, unknown>;
}

export interface WeatherCamItem {
    City: string;
    Distance: number;
    Id: string;
    Image: string;
    Images: null | string[];
    IsHD: true;
    Lat: number;
    Lng: number;
    Name: string;
    State: string;
    Thumbnail: string;
}

export interface WeatherCam {
    listIsRefreshing: boolean;
    detailIsRefreshing: boolean;
    list: WeatherCamItem[];
    lastError: Record<string, unknown>;
}

export interface Hurricane {
    hurricanesGeoJson: HurricanesGeoJSON;
    legend: HurricaneLegend;
    hurricaneIsFetching: boolean;
    hurricaneLegendIsFetching: boolean;
    lastLegendError: LastErrorType;
    lastError: LastErrorType;
    hurricanes: CurrentHurricane[];
    currentTrackPointGeoJson: CurrentTrackPointGeoJSON;
    currentTrackPoint: CurrentTrackPoint;
    currentHurricaneIndex: number;
    currentHurricane: CurrentHurricane;
}

export type HurricaneCategoryNumber = 1 | 2 | 3 | 4 | 5;
export type LastErrorType = Record<string, unknown> | string;

export interface CurrentHurricane {
    id: string;
    name: string;
    type: typeof HurricaneTypes[keyof typeof HurricaneTypes];
}

export const HurricaneTypes = { 
    hurricane: 'Hurricane',
    tropicalDepression: 'Tropical Depression',
    tropicalStorm: 'Tropical Storm'
} as const;

export type DistanceUnit = keyof typeof distanceUnits; // "miles" | "kilometers"
export type SpeedUnit = keyof typeof speedUnits; // "mph" | "kph" | "knots" | "mps"
export type HurricaneCategory = `Category ${HurricaneCategoryNumber}`; // "Category 1" | "Category 2" | "Category 3" | "Category 4" | "Category 5"

export interface CurrentTrackPoint {
    distance: number;
    distanceUnit: DistanceUnit; // "miles" | "kilometers"
    wind: number;
    windUnit: SpeedUnit;
    gust: number;
    gustUnit: SpeedUnit;
    pressure: number;
    pressureUnit: 'inHg' | 'mbar';
    directionUnit: DistanceUnit; // "miles" | "kilometers"
    coordinates: string;
    direction: string;
    category: HurricaneCategory;
    datetimeUtc: number;
    hurricaneType: typeof HurricaneTypes[keyof typeof HurricaneTypes];
    speed: number;
    speedUnit: SpeedUnit;
    pointType: typeof PointType[keyof typeof PointType];
    t: number;
    dtype: number;
    categoryNumber: HurricaneCategoryNumber;
    basin: typeof Basin[keyof typeof Basin];
}

export interface CurrentTrackPointGeoJSON {
    type: 'Feature';
    geometry: Geometry;
    properties: CurrentTrackPoint;
}

export const PointType = {
    currentPosition: 'CURRENT POSITION',
    historicalPosition: 'HISTORICAL POSITION',
    forecastPosition: 'FORECAST POSITION'
} as const;

export const Basin = {
    atlantic: 'Atlantic',
    easternPacific: 'Eastern Pacific',
    centralPacific: 'Central Pacific'
} as const;

export const GeometryType = {
    point: 'Point',
    lineString: 'LineString',
    polygon: 'Polygon'
} as const;

export interface Geometry {
    type: typeof GeometryType[keyof typeof GeometryType];
    coordinates: number[];
    properties: GeometryProperties;
}

export interface GeometryProperties {
    n?: string;
    id: string;
    bn?:typeof Basin[keyof typeof Basin];
    dt?:  number;
    lat?: number;
    lon?: number;
    tn?: typeof HurricaneTypes[keyof typeof HurricaneTypes];
    t?: number;
    c?: number | null;
    cs?: string | null;
    w?: number;
    wu?: number;
    g?: number;
    gu?: number;
    dir?: number | null;
    diru?: number;
    s?: number | null;
    su?: number;
    p?: number | null;
    pu?: number;
    dis?: number;
    diss?: string;
    disu?: number;
    dtype: number;
    dtypes?: typeof PointType[keyof typeof PointType];
    iid?: string;
    index: number;
}

export interface HurricanesGeoJSON {
    type: string;
    features: Feature[];
    responseTime: number;
}

export interface Feature {
    type: 'Feature';
    geometry: FeatureGeometry;
    properties: GeometryProperties;
}

export interface FeatureGeometry {
    type: typeof GeometryType[keyof typeof GeometryType];
    coordinates: Array<Array<number[] | number> | number>;
}

export interface HurricaneLegend {
    val: HurricaneLegendVal;
    responseTime: number;
}

export interface HurricaneLegendVal {
    layerId: 'Hurricane';
    layerComponents: HurricaneLayerComponent[];
}

export interface HurricaneLayerComponent {
    layerComponentId: string;
    legendData: HurricaneLegendData;
}

export interface HurricaneLegendData {
    colors: HurricaneLegendColor[];
    stationTypes: HurricaneStationType[];
}

export interface HurricaneLegendColor {
    unitType: string;
    unitName: string;
    unitAbbreviation: string;
    colorStops: HurricaneLegendColorStop[];
}

export interface HurricaneLegendColorStop {
    label: string;
    color: string;
}

export interface HurricaneStationType {
    id: number;
    type: string;
}

export interface hurricaneOutlookSummaryStateType {
    data: HurricaneOutlookSummaryPayload;
    isFetching: boolean;
    requestError: Record<string, unknown>;
}

export type HurricaneOutlookSummaryType = HurricaneOutlookSummaryPayload;

export interface HurricaneOutlookBulletinStateType {
    data?: HurricaneOutlookBulletinPayload;
    isFetching?: boolean;
    requestError?: Record<string, unknown>;
}

export type HurricaneOutlookBulletinType = HurricaneOutlookBulletinPayload;

export interface HurricaneBulletinImageIds {
    at: string;
    cp: string;
    ep: string; 
}

export interface ComplianceRequirements {
    dataPrivacyEnabled: boolean;
    sensitiveDataRestrictionEnabled: boolean;
    requiresGDPRCompliance: boolean;
    disableAds: boolean;
    filterSensitiveAdParameters: boolean;
    responseTime: number;
}

export interface PageMetaData {
    Title: string;
    NationalPageTitle?: string | null;
    LocalizedPageTitle?: string | null;
    Description: string;
    NationalMetaDescription?: string | null;
    LocalizedMetaDescription?: string | null;
    Keywords: string;
    AndroidFreeAppID: string;
    AndroidEliteAppID: string;
    IOSFreeAppID: string;
    IOSEliteAppID: string;
    MacOSFreeAppID: string;
    WindowsFreeAppID: string;
    FacebookAppID: string;
    FacebookPageID: string;
    Social: SocialPageMetaData;
    Canonical: string;
    Robots?: string | null;
    HasNewSeoMetaInConfig?: boolean | null;
    HasPageConfigCanonical?: boolean | null;
}

export interface SocialPageMetaData {
    Title: string;
    Description: string;
    Image: string;
    CardType: string;
    PageType: string;
    Url: string;
    LastModifiedDate?: string | null;
    PublishedDate?: string | null;
}

export interface PageSetup {
    Header: string;
    Footer: string;
    BackgroundUrl?: string | null;
}

export interface Legal {
    complianceRequirements: ComplianceRequirements;
}

export interface FavoriteLocationsState {
    favoriteLocations: FavoriteLocation[];
}

export const PollingTimes = {
    'Alerts': 60,
    'Notifications': 60,
    'Almanac': 3600,
    'DailyForecast': 3600,
    'DailyForecasts': 300,
    'Energy': 3600,
    'GIV': 30,
    'HourlyForecast': 900,
    'HourlyForecastPeriods': 300,
    'Life': 3600,
    'Lightning': 90,
    'LocalWeatherCameras': 1800,
    'Metadata': 36000,
    'Observation': 300,
    'PageLayouts': 36000,
    'Photos': 3600,
    'Pollen': 3600,
    'Popups': 900,
    'Sitemap': 36000,
    'SitemapCities': 36000,
    'SitemapContinents': 36000,
    'SitemapCountries': 36000,
    'SitemapStateCities': 36000,
    'SitemapStateTop200Cities': 36000,
    'SitemapStates': 36000,
    'Stories': 30,
    'TrafficCameras': 1800,
    'UV': 3600,
    'UvPeriods': 3600,
    'WeatherCameras': 1800
} as const;

export interface AppConfigType {
    WB_AppKey: string;
    AppVersion: string;
    ApplicationName: string;
    Environment: string;
    IntercomAppId: null;
    DisableAds: boolean;
    DefaultMapLayerId: string;
    DefaultAdRefeshIntervalSeconds: number;
    LeadingEdgeDebounceDelay: number;
    MapsPageRefreshTimer: number;
    KochavaAppId: string;
    KochavaClickURL: string;
    WebBackendBaseURL: string;
    IosSmartAppBannerURL: string;
    AndroidSmartAppBannerURL: string;
    Pulse: Pulse;
    VideoPreRollUrls: VideoPreRollUrls;
    VideoPrerollAdClient: string;
    VideoPrerollOffset: string;
    VideoPrerollSkipOffset: string;
    VideoPrerollVPaidControls: string;
    VideoPrerollAutoplayAdsMuted: string;
    FeaturedVideoPrerollPlacementId: string;
    NationalOutlookVideoPrerollPlacementId: string;
    StoriesVideoPrerollPlacementId: string;
    BaseURLs: BaseURLs;
    ForecastIconContent: ForecastIconContent;
    HurricaneOutlookDetailsImages: HurricaneBulletinImageIds;
    Mapbox: Mapbox;
    PollingTimes: typeof PollingTimes;
    SupportEmailAddress: string;
    CareersLink: string;
    EducationVideoPageURL: string;
    PressLinkActive: boolean;
    IOSAppDownloadLink: string;
    MacosAppDownloadLink: string;
    AndroidAppDownloadLink: string;
    DesktopAppDownloadLink: string;
    EnableDesktopAppDownloadLink: boolean;
    TaboolaPages: string[];
    BotAppInstanceId: string;
    MinimumAppInstanceRegistrationDateUtc: string;
    StoryCacheTimeout: number;
    VideoEnabled: boolean;
    LargeVideoExt: string;
    SmallVideoExt: string;
    Platform: PlatformType;
    Locales: Locale[];
    Taboola: Taboola;
    TaboolaInfo: TaboolaInfo[];
    VideoAdRules: VideoAdRule[];
    AdBlockDetectionEnabled: boolean;
    EmailOptInDialog: EmailOptInDialog;
    GTMediaCampaign: GTMediaCampaign;
    GoogleTagManager: GoogleTagManager;
    Comscore: Comscore;
    Firebase: Firebase;
    Boltive: Boltive;
    GamTestAd: GamTestAd;
    NewPrivacyPolicyMockData: NewPrivacyPolicyMockData;
    Persistent320x50AdForMobileWeb: Persistent320x50AdForMobileWeb;
    SparkMap: EnvBaseURL;
    EnableNewPhotosPopupSection: boolean;
    AirQualityAttributionUrl: string;
    DisclosureRequestEmail: string;
    DeletionRequestEmail: string;
    PrivacyRightsEmail: string;
    CcpaInfoEmail: string;
    PrivacyRightsLink: string;
    PrivacyStatement: string;
    MyIdfaLink: string;
    EnableBreakingNewsTest: boolean;
    EnableNewSmartBanner: boolean;
    EnableNewFooterUIWithCMPIntegration: boolean;
    UsPrivacyManagerId: number;
    SourcePointCMPAccountId: number;
    SourcePointCMPPropertyId: number;
    SourcePointCMPPropertyHref: string;
    SourcePointCMPBaseEndpoint: string;
    SourcePointCMPCampaignEnv: string;
    UseAdBlockerTestScript: boolean;
    TrackerExclusionList: string[];
    PageContentDoesNotRequireActiveLocationData: string[];
    AuthClientSettings: AuthClientSettings;
    IsAuthV2Enabled: boolean;
    IsAqiV2Enabled: boolean;
    IsOutdoorSportsEnabled: boolean;
    IsComplianceApiV3Enabled: boolean;
    IsHurricaneSectionEnabled: boolean;
    Enable10DayForecastPageOnWeb2: boolean;
    EnableHourlyForecastPageOnWeb2: boolean;
    EnableNewsLandingPageOnWeb2: boolean;
    EnableLifePollenPageOnWeb2: boolean;
    EnableLifeColdFluPageOnWeb2: boolean;
    EnableAirQualityLandingPageOnWeb2: boolean;
    EnableVideoLandingPageOnWeb2: boolean;
    EnableAlertsAQIPageOnWeb2: boolean;
    EnableAlertsSevereStormPageOnWeb2: boolean;
    EnableAlertsAllPageOnWeb2: boolean;
    EnableFireAlertsPageOnWeb2: boolean;
    EnableHurricaneAlertsPageOnWeb2: boolean;
    EnableTrafficCamerasPageOnWeb2: boolean;
    EnableWeatherCamerasPageOnWeb2: boolean;
    EnableAlertsPollenPageOnWeb2: boolean;
    EnableMapsPageOnWeb2: boolean;
    EnableSourceParameterOnStoriesLinks: boolean;
    EnableAlertsWeatherPageOnWeb2: boolean;
    EnableAlertsLightningPageOnWeb2: boolean;
    EnableEducationPageOnWeb2: boolean;
    EnableFaqAndFaqGeneralPageOnWeb2: boolean;
}

export interface AuthClientSettings {
    Authority: string;
    Scope: string;
    ResponseType: string;
    ClientId: string;
    PopupRedirectBaseURL: string;
    SilentRedirectBaseURL: string;
    RevokeAccessTokenOnSignout: boolean;
    EnableLogging: boolean;
}

export interface BaseURLs {
    Giv: string;
    Stories: EnvBaseURL;
    SGiv: string;
    GivDomains: string[];
    GivPresentation: string;
    GivMetadata: string;
    Traffic: EnvBaseURL;
    Pollen: EnvBaseURL;
    SnowSki: EnvBaseURL;
    UV: EnvBaseURL;
    Locations: EnvBaseURL;
    AQI: EnvBaseURL;
    Cameras: EnvBaseURL;
    Photos: EnvBaseURL;
    ContentMgmt: EnvBaseURL;
    Observations: EnvBaseURL;
    LifeStyles: EnvBaseURL;
    Almanac: EnvBaseURL;
    Forecast: EnvBaseURL;
    Alerts: EnvBaseURL;
    OutdoorSports: EnvBaseURL;
    Notifications: EnvBaseURL;
    Lightning: EnvBaseURL;
    Legal: EnvBaseURL;
    Video: EnvBaseURL;
    Logging: EnvBaseURL;
    Hurricane: EnvBaseURL;
    HurricaneOutlook: EnvBaseURL;
    Fire: EnvBaseURL;
    AdTargeting: EnvBaseURL;
    Feedback: EnvBaseURL;
    Content: EnvBaseURL;
    EmLogin: string;
}

export interface EnvBaseURL {
    Prod: string;
    Stage: string;
    QA: string;
}

export interface EmailOptInDialog {
    Enabled: boolean;
    ScriptSourceUrl: string;
}

export interface ForecastIconContent {
    ForecastIconSet: string;
    ForecastIconSize: string;
    IconContentResourceList: string;
}

export interface GTMediaCampaign {
    IsEnabled: boolean;
    HomePagePixelImgSourceUrl: string;
    AppDownloadPagePixelImgSourceUrl: string;
}

export interface GoogleTagManager {
    IsEnabled: boolean;
    GTMContainerId: string;
    PagesWithGTMTitleTags: string[];
}

export type Comscore = {
    IsEnabled: boolean;
    TagTypeC1Param: string;
    PublisherIdC2Param: string;
    EnableFirstPartyCookie: boolean;
    FirstPartyCookieName: string;
}
export interface Boltive {
    IsEnabled: boolean;
    ClientId: string;
    SpecificPageRouteNameToEnableForQA: string;
    FirebaseRemoteConfigKey: string;
}

export interface Firebase {
    IsEnabled: boolean;
    ApiKey: string;
    AuthDomain: string;
    ProjectId: string;
    StorageBucket: string;
    MessagingSenderId: string;
    AppId: string;
    MinimumFetchIntervalMillis: number;
    GoogleAnalyticsMeasurementId: string;
    EnableGoogleAnalytics: boolean;
}

export interface GamTestAd {
    IsEnabled: boolean;
    ClientId: string;
    GamTestAdFirebaseRemoteConfigKey: string;
}

export interface Locale {
    language: string;
    cultureName: string;
}

export interface Mapbox {
    Maps: MapboxMaps;
    PublicKey: string;
}

export interface MapboxMaps {
    Main: string;
    Alerts: string;
    Spark: string;
}

export interface NewPrivacyPolicyMockData {
    IsEnabled: boolean;
    DataPrivacyEnabled: boolean;
    SensitiveDataRestrictionEnabled: boolean;
}

export interface Platform {
    Header: boolean;
    Ads: boolean;
    Footer: boolean;
    SmartBanner: boolean;
    Navigation: boolean;
    ShowMapOverlays: boolean;
    Notifications: boolean;
    UseOfCookiesNotification: boolean;
    TermsAndConditionsNotification: boolean;
    ShowEmailOptInWidget: boolean;
    EnableKochavaSdk: boolean;
}

export interface Pulse {
    ID: string;
    Secret: string;
}

export interface Taboola {
    Disabled: boolean;
    NowScreenDisabled: boolean;
    DailyScreenDisabled: boolean;
    HourlyScreenDisabled: boolean;
    StoryPopupDisabled: boolean;
    LightHouseQueryParamDisableValue: string;
}

export interface TaboolaInfo {
    PageId: string;
    ContainerId: string;
    PlacementId: string;
    Feed: TaboolaFeedType;
    Position: TaboolaPlacementPosition;
    Mode: TaboolaFeedMode;
    TargetType: TaboolaTargetType;
}

export type TaboolaFeedType = 'category' | 'other' | 'article';

export type TaboolaFeedMode = 'thumbnails-a';

export type TaboolaPlacementPosition = 'mid_article' | 'below_content';

export type TaboolaTargetType = 'mix';

export interface VideoAdRule {
    frequency: number;
}

export interface VideoPreRollUrls {
    nationalOutlook: string;
    stories: string;
    videos: string;
}

export type GlobalAdConfigs = { 
    pageAdConfigs: PageAdConfig[];
    memberId?: number;
}

export interface PageAdConfig {
    pageId: string;
    slots: Slot[];
}

export interface Slot {
    slotId: string;
    tagId: number;
    adProviderConfig: AdProviderConfig;
    prebidSlotIds: PrebidSlotIDS | null;
    includedPrebidBidders: IncludedPrebidBidder[] | null;
    excludedPrebidBidders: ExcludedPrebidBidder[] | null;
    screenSizes: string[];
}

export interface AdProviderConfig {
    allowSmallerSizes: boolean;
    allowedFormats: AllowedFormat[];
    sizes: Array<number[]>;
    prebid: boolean;
    enableSafeFrame?: boolean;
}

export type AllowedFormat = 'video' | 'banner';

export type ExcludedPrebidBidder = 'justpremium' | 'rubicon';

export type IncludedPrebidBidder = 'nobiddersallowed';

export interface PrebidSlotIDS {
    appnexus?: string;
    pubmatic?: string;
    ix?: string;
    emx_digital?: string;
    aol?: string;
    onemobile?: string;
    '33across'?: string;
    triplelift?: string;
}